/**
 * The profile ✦/history affordance (enrichment→profile integration §6). Sits next to a ledger-backed
 * identity field (name / title / seniority / org types) and shows a small provenance chip for the
 * CURRENT value's source — teal ✦ = machine-derived (enrichment), blue ✎ = human-edited (with the
 * actor + date). Click to expand the full observation history (value · source · actor · date), so an
 * operator can see e.g. "name updated to 'Sacramento County Employees' Retirement System' by brad on
 * 2026-07-20", with the prior enrichment value beneath it. `attr` is the per-key block from
 * GET /api/crm/{kind}/{id}/attribute-history; renders nothing when there is no observation.
 */
import { useState } from 'react';

import type { CrmAttrHistory } from '../../types';
import styles from './crm.module.css';

const fmtDate = (iso: string | null): string => (iso ? iso.slice(0, 10) : '');
// D-2: the enrichment badge's hover reads "Enriched on mm/dd/yy" — US short date, per the owner's wording.
const fmtUS = (iso: string | null): string => {
  if (!iso) return '';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${m}/${d}/${y.slice(2)}`;
};
const srcLabel = (source: string): string =>
  source === 'user' ? 'edited' : source === 'enrichment' ? 'enrichment' : source;

export function CrmAttributeProvenance({ attr, onReject, rejectPending, onEdit, editPending, editOptions }: {
  attr: CrmAttrHistory | undefined;
  /** D-2 phase 1: present only where the field is rejectable (person title/division/seniority/linkedin).
   *  Receives the operator's optional note; the parent owns the mutation. */
  onReject?: (note: string) => void;
  rejectPending?: boolean;
  /** D-2 phase 2: inline edit — receives the new value; the parent owns the mutation. Unlike Reject
   *  (enrichment-only), Edit is offered for ANY source: a human edit is corrected by another edit. */
  onEdit?: (value: string) => void;
  editPending?: boolean;
  /** Constrained-taxonomy fields (seniority) render a select instead of free text. */
  editOptions?: readonly string[];
}) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState('');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const cur = attr?.current;
  if (!cur) return null;
  const isUser = cur.source === 'user';
  const isEnrichment = cur.source === 'enrichment';
  // D-2 (owner, 2026-07-22/24): an enrichment-sourced field shows JUST a green ✦ — no literal word
  // "enrichment" — and the hover tooltip reads "Enriched on mm/dd/yy". Human edits keep the blue ✎ with
  // actor + date; other machine sources (resolver/merge) keep the generic wording.
  const title = isUser
    ? `edited${cur.actor ? ` by ${cur.actor}` : ''}${cur.observed_at ? ` on ${fmtDate(cur.observed_at)}` : ''}`
    : isEnrichment
      ? `Enriched${cur.observed_at ? ` on ${fmtUS(cur.observed_at)}` : ''}`
      : `machine-derived (${srcLabel(cur.source)})${cur.observed_at ? ` ${fmtDate(cur.observed_at)}` : ''}`;
  const hist = attr?.history ?? [];
  return (
    <span className={styles.provWrap}>
      <button
        type="button"
        className={`${styles.provChip} ${isUser ? styles.provChipUser : isEnrichment ? styles.provChipEnrich : ''}`}
        title={title}
        aria-expanded={open}
        aria-label={`provenance: ${title}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        {isUser ? '✎ edited' : isEnrichment ? '✦' : `✦ ${srcLabel(cur.source)}`}
      </button>
      {open && (
        <div className={styles.provHistory}>
          {onEdit && (
            <div className={styles.provReject}>
              {!editing ? (
                <button type="button" className={styles.provRejectBtn}
                        onClick={(e) => { e.stopPropagation(); setDraft(cur.value ?? ''); setEditing(true); }}>
                  Edit value…
                </button>
              ) : (
                <span onClick={(e) => e.stopPropagation()}>
                  {editOptions ? (
                    <select className={styles.provRejectNote} value={draft} disabled={editPending}
                            aria-label="new value"
                            onChange={(e) => setDraft(e.target.value)}>
                      {!editOptions.includes(draft) && <option value={draft}>{draft || '—'}</option>}
                      {editOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input className={styles.provRejectNote} placeholder="new value" value={draft}
                           aria-label="new value"
                           onChange={(e) => setDraft(e.target.value)} disabled={editPending} />
                  )}
                  <button type="button" className={styles.provRejectBtn}
                          disabled={editPending || !draft.trim()}
                          onClick={() => onEdit(draft.trim())}>
                    {editPending ? 'Saving…' : 'Save'}
                  </button>
                  <button type="button" className={styles.provRejectBtn} disabled={editPending}
                          onClick={() => { setEditing(false); setDraft(''); }}>×</button>
                </span>
              )}
            </div>
          )}
          {onReject && cur.source === 'enrichment' && (
            <div className={styles.provReject}>
              {!confirming ? (
                <button type="button" className={styles.provRejectBtn}
                        onClick={(e) => { e.stopPropagation(); setConfirming(true); }}>
                  Reject this value…
                </button>
              ) : (
                <span onClick={(e) => e.stopPropagation()}>
                  <input className={styles.provRejectNote} placeholder="why? (optional)" value={note}
                         onChange={(e) => setNote(e.target.value)} disabled={rejectPending} />
                  <button type="button" className={styles.provRejectBtn} disabled={rejectPending}
                          onClick={() => onReject(note)}>
                    {rejectPending ? 'Rejecting…' : 'Confirm reject'}
                  </button>
                  <button type="button" className={styles.provRejectBtn} disabled={rejectPending}
                          onClick={() => { setConfirming(false); setNote(''); }}>×</button>
                </span>
              )}
            </div>
          )}
          {hist.length === 0 ? (
            <div>No history.</div>
          ) : (
            hist.map((o, i) => (
              <div key={i} className={o.reverted_at ? styles.provReverted : undefined}>
                <span className={styles.provVal}>{o.value ?? '—'}</span>
                {' · '}
                {srcLabel(o.source)}
                {o.actor ? ` by ${o.actor}` : ''}
                {o.observed_at ? ` · ${fmtDate(o.observed_at)}` : ''}
                {o.reverted_at ? ' (reverted)' : ''}
              </div>
            ))
          )}
        </div>
      )}
    </span>
  );
}
